import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { PlanStep } from '../../../../types/generated/graphql';
import { colors } from '../../../../theme/colors';
import StepInteractionFeed, { FeedItem } from '../StepInteractionFeed';
import CollapsibleStepInfo from '../CollapsibleStepInfo';

interface ReadyToCompleteContentProps {
  step: PlanStep;
  feedItems: FeedItem[];
  onMarkComplete: () => void;
  isMarkingComplete?: boolean;
}

/**
 * READY_TO_COMPLETE mode content.
 * All requirements are satisfied and no active delegation exists.
 *
 * Renders:
 * - Compact confirmation summary ("All requirements satisfied")
 * - Primary CTA: Mark Complete
 *
 * Does NOT render:
 * - Delegation UI
 * - Chat input (unless post-completion chat is supported)
 */
export const ReadyToCompleteContent: React.FC<ReadyToCompleteContentProps> = ({
  step,
  feedItems,
  onMarkComplete,
  isMarkingComplete = false,
}) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      {/* Collapsible Step Info - auto-expands first time */}
      {step.description && (
        <CollapsibleStepInfo stepId={step.id} description={step.description} />
      )}

      {/* Completion prompt - subtle with left accent */}
      <Box
        sx={{
          px: 2,
          py: 2,
          mx: 2,
          mt: 1,
          borderLeft: `3px solid ${colors.green[600]}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <CheckCircleIcon sx={{ fontSize: 20, color: colors.green[600] }} />
          <Typography variant="body2" sx={{ fontWeight: 600, color: colors.green[700] }}>
            Ready to complete
          </Typography>
        </Box>

        <Button
          variant="contained"
          size="small"
          onClick={onMarkComplete}
          disabled={isMarkingComplete}
          startIcon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
          sx={{
            bgcolor: colors.green[600],
            '&:hover': { bgcolor: colors.green[700] },
            fontWeight: 500,
          }}
        >
          {isMarkingComplete ? 'Completing...' : 'Mark Complete'}
        </Button>
      </Box>

      {/* Feed for context - minimized */}
      <Box sx={{ borderTop: `1px solid ${colors.border}` }}>
        <StepInteractionFeed items={feedItems} stepTitle={step.title} />
      </Box>
    </Box>
  );
};

export default ReadyToCompleteContent;
